import React, { useEffect, useRef, FC, MouseEvent } from 'react';
import injectSheet from 'react-jss';
import { TweenMax, Back } from 'gsap';
import classNames from 'classnames';
import checkMark from './checkmark.svg';

const BUTTON_HEIGHT = 72;
const BUTTON_MARGIN = 15;
const BUTTON_PADDING = 24;

const styleSheet = (theme: any) => ({
  button: { textAlign: 'left', ...theme.button.default },
  error: { ...theme.button.error },
  icon: {
    width: '23px',
    height: '23px',
    border: 'solid 2px #b7c1e1',
    borderRadius: '100%',
    display: 'inline-block',
    float: 'left',
    backgroundColor: 'rgba(255,255,255,0)',
    marginRight: '18px',
  },
  arrow: {
    opacity: 0,
    transform: 'scale(0)',
    position: 'relative',
    left: '2px',
  },
  keyboardShortcutContainer: {
    objectFit: 'contain',
    fontFamily: "'CircularStd', sans-serif",
    fontSize: '12px',
    fontWeight: '500',
    fontStyle: 'normal',
    fontStretch: 'normal',
    lineHeight: 'normal',
    letterSpacing: 'normal',
    color: '#b7c1e1',
    position: 'absolute',
    top: `${BUTTON_HEIGHT + 20}px`,
    left: `${BUTTON_MARGIN + BUTTON_PADDING}px`,
  },
  keyboardShortcutIndicator: {
    width: '17px',
    height: '17px',
    borderRadius: '2px',
    padding: '1px 4px',
    border: 'solid 1px #b7c1e1',
    marginLeft: '3px',
  },
});

interface ButtonProps {
  onClick: (enabled: object) => void;
  classes: any;
  enabled?: boolean | undefined;
  value: string;
  error?: boolean;
  index?: string;
  enableKeyboardShortcut?: true;
  children?: any;
}

const Button: FC<ButtonProps> = (props: ButtonProps) => {
  const {
    onClick,
    classes,
    enabled,
    value,
    error,
    index,
    enableKeyboardShortcut,
    children,
  } = props;

  const handleClick = () => {
    // animate.showCheckMark(enabled);
    onClick({ enabled: enabled !== true, value });
  };

  const shortcutKey = () => {
    const buttonIndex = index;
    if (typeof buttonIndex === 'number') {
      return buttonIndex + 1;
    }
    return null;
  };

  const button = useRef(null);
  const icon = useRef(null);
  const arrow = useRef(null);

  const animate = {
    showCheckMark: (isEnabled: boolean | undefined) => {
      if (isEnabled) {
        TweenMax.to(icon, 0.35, {
          borderColor: '#6a9fef',
          backgroundColor: '#6a9fef',
          scale: 0,
          ease: Back.easeIn,
        });
        TweenMax.to(icon, 0.35, {
          scale: 1,
          delay: 0.35,
          ease: Back.easeOut,
        });
        TweenMax.to(arrow, 0.35, {
          opacity: 1,
          scale: 1,
          transformOrigin: '50% 50%',
          delay: 0.4,
          ease: Back.easeOut,
        });
        animate.enter();
      } else {
        TweenMax.to(icon, 0.35, {
          delay: 0.25,
          borderColor: '#b7c1e1',
          backgroundColor: 'transparent',
        });
        TweenMax.to(arrow, 0.35, {
          opacity: 0,
          scale: 0,
          transformOrigin: '50% 50%',
          ease: Back.easeIn,
        });
        TweenMax.delayedCall(0.35, animate.leave);
      }
    },
    enter: () => {
      TweenMax.to(button, 0.35, {
        color: '#fff',
        backgroundColor: '#085fe5',
        borderColor: '#085fe5',
        boxShadow: '0 4px 10px 0 rgba(8, 95, 229, 0.5)',
      });
    },
    leave: () => {
      TweenMax.to(button, 0.35, {
        color: '#6373a4',
        backgroundColor: '#fff',
        borderColor: '#b7c1e1',
        boxShadow: '0 4px 10px 0 rgba(8, 95, 229, 0)',
      });
    },
  };

  useEffect(() => {
    window.addEventListener('keypress', ({ key }) => {
      if (parseInt(key, 10) === shortcutKey()) {
        handleClick();
      }
    });
    if (enabled !== undefined) animate.showCheckMark(enabled);
    return () => {
      window.removeEventListener('keypress', () => handleClick());
    };
  }, [handleClick]);

  const onMouseEnterHandler = (event: any) => {
    event.stopPropagation();
    event.preventDefault();
    animate.enter();
  };

  const onMouseLeaveHandler = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    animate.leave();
  };

  return (
    <button
      onBlur={enabled ? undefined : animate.leave}
      onFocus={enabled ? undefined : animate.enter}
      ref={button}
      className={classNames(classes.button, {
        [classes.error]: error,
      })}
      onClick={() => handleClick()}
      onMouseEnter={enabled ? undefined : onMouseEnterHandler}
      onMouseLeave={enabled ? undefined : onMouseLeaveHandler}
      type="button"
    >
      <div className={classes.icon} ref={icon}>
        <img
          className={classes.arrow}
          width="18"
          height="15"
          src={checkMark}
          ref={arrow}
          alt="toggle button"
        />
      </div>
      {children}
      {enableKeyboardShortcut ? (
        <div className={classes.keyboardShortcutContainer}>
          Press
          <span className={classes.keyboardShortcutIndicator}>
            {shortcutKey()}
          </span>
        </div>
      ) : null}
    </button>
  );
};

Button.defaultProps = {
  enabled: undefined,
  error: undefined,
  index: undefined,
  enableKeyboardShortcut: undefined,
  children: undefined,
};

export default injectSheet(styleSheet)(Button);
